import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import type { RemoveDuoData, RemoveDuoResponse } from 'generated/openapi';

import { getDuoDetailsKey } from '@/features/duos/hooks/useGetDuoDetails/getDuoDetails.options';
import { PrivateApi } from '@/shared/api/httpClient';
import type { ReplacePathParams } from '@/shared/utils/pathParams';

import { useInvalidateDuos } from '../useGetDuos';

export interface UseRemoveDuoMutationParams {
  userId: string;
}

export interface UseRemoveDuoProps extends UseMutationOptions<
  RemoveDuoResponse,
  unknown,
  UseRemoveDuoMutationParams
> {}

const removeDuoUrl = (
  userId: string,
): ReplacePathParams<RemoveDuoData['url']> => `duos/${userId}`;

export function useRemoveDuo(props: UseRemoveDuoProps = {}) {
  const queryClient = useQueryClient();
  const invalidateDuos = useInvalidateDuos();
  const { onSuccess, ...restProps } = props;

  return useMutation({
    mutationFn: async ({ userId }: UseRemoveDuoMutationParams) => {
      await PrivateApi.delete(removeDuoUrl(userId));
    },
    ...restProps,
    onSuccess: async (data, variables, context, mfContext) => {
      // Remove the specific duo query from cache to prevent refetch of deleted entity
      queryClient.removeQueries({
        queryKey: getDuoDetailsKey({ userId: variables.userId }),
      });
      void invalidateDuos();
      if (onSuccess) await onSuccess(data, variables, context, mfContext);
    },
  });
}
